/**
 * Pure aggregation/normalization helpers for flock telemetry. Kept free of
 * Angular so both platforms (and tests) consume them directly.
 */
import {
  AccountRollup,
  ContextBand,
  ContextRow,
  ContextSnapshot,
  CONTEXT_THRESHOLD_PCT,
  QuotaRow,
  TEAM_PREMIUM_FIVE_HOUR_CAP,
  TEAM_PREMIUM_WEEKLY_CAP,
  UsageBand,
  WindowSummary,
} from './flock-telemetry.models';
import {
  projectFromLastApiReset,
  WEEKLY_WINDOW_MS,
} from './quota-reset-projection';

export function classifyUsageBand(pct: number): UsageBand {
  if (pct < 50) return 'cool';
  if (pct < 75) return 'warm';
  if (pct < 90) return 'hot';
  return 'critical';
}

/**
 * Quota timestamps come from SQL as UTC wall time WITHOUT an offset suffix
 * ("2026-08-10T04:38:12.967"). `new Date(...)` parses a suffix-less ISO string
 * as LOCAL time, which skews every countdown and reset sort by the viewer's
 * UTC offset. Append Z only when no offset is present.
 */
export function toUtcIso(value: string | null): string | null {
  if (!value) return null;
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
}

/** Epoch ms for a quota timestamp, UTC-normalized. Null in, null out. */
export function parseUtcMs(value: string | null): number | null {
  const iso = toUtcIso(value);
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** The reset that actually governs the row: manual override beats scanner. */
export function effectiveResetIso(row: QuotaRow): string | null {
  return row.manualResetOverride ?? row.resetAt;
}

/**
 * A row's reset is AUTHORITATIVE when it derives from the account's real
 * rate_limits: the rendered status line ('statusline') or the payload
 * cross-check ('payload'). A null source (transcript-inferred, per home) and
 * 'estimate' are lookback guesses. Multi-home accounts mix the two, and a
 * wrong inferred reset must not override the true one (WI 31735). */
export function isAuthoritativeQuotaSource(
  row: Pick<QuotaRow, 'source'>
): boolean {
  const s = row.source?.toLowerCase() ?? null;
  return s === 'statusline' || s === 'payload' || s === 'grok' || s === 'codex';
}

/**
 * A row is CURRENT while its effective reset is still in the future. A row
 * with no reset at all has no live window (no activity observed), so it is
 * not current. Expired rows are history: their token counts must not be
 * summed into "this window" totals.
 */
export function isQuotaRowCurrent(row: QuotaRow, now = Date.now()): boolean {
  const resetMs = parseUtcMs(effectiveResetIso(row));
  return resetMs !== null && resetMs > now;
}

function minIso(values: (string | null)[]): string | null {
  const present = values.filter((v): v is string => !!v);
  if (present.length === 0) return null;
  return present.sort()[0];
}

function maxIso(values: (string | null)[]): string | null {
  const present = values.filter((v): v is string => !!v);
  if (present.length === 0) return null;
  return present.sort().reverse()[0];
}

function maxLastUpdated(rows: QuotaRow[]): string | null {
  const present = rows
    .map((r) => r.lastUpdatedAt)
    .filter((v): v is string => !!v);
  if (present.length === 0) return null;
  return present.sort().reverse()[0];
}

/** capturedAt when it parses, else lastUpdatedAt: the stamp a reading is aged by (WI 38037, review 111725). */
function observationStamp(row: QuotaRow): string | null {
  return parseUtcMs(row.capturedAt ?? null) !== null
    ? (row.capturedAt ?? null)
    : row.lastUpdatedAt;
}

function observationTime(row: QuotaRow): number {
  return parseUtcMs(observationStamp(row)) ?? -Infinity;
}

/**
 * Summarize one account's rows for one window type, counting ONLY rows whose
 * window is still current. Before 2026-08-10 this summed every row ever
 * scanned, so an account's "5h tokens" included windows that expired weeks
 * ago and the number was meaningless as a live gauge.
 */
export function summarizeQuotaWindow(
  windowRows: QuotaRow[],
  cap: number,
  now = Date.now(),
  cadenceMs: number | null = null
): WindowSummary {
  const currentRows = windowRows.filter((r) => isQuotaRowCurrent(r, now));
  const tokenCount = currentRows.reduce((s, r) => s + (r.tokenCount ?? 0), 0);
  const messageCount = currentRows.reduce(
    (s, r) => s + (r.messageCount ?? 0),
    0
  );
  const earliestFirst = minIso(currentRows.map((r) => r.firstMessageAt));
  // The account's reset. A manual override is a deliberate operator
  // correction, so any override beats every scanner row. Among scanner rows
  // take the LATEST reset, not the earliest: rows written without an
  // API-sourced reset carry the lookback estimate (first observed message +
  // window length), which systematically UNDERSHOOTS on a continuously
  // active account ("resets in minutes" for a window really days out, Tim
  // 2026-08-10). API-stamped rows carry the true per-account reset, which is
  // never earlier than any estimate for the same live window.
  const overrideResets = currentRows
    .filter((r) => r.manualResetOverride !== null)
    .map((r) => r.manualResetOverride);
  // WI 31735: when any current row is authoritative (payload/statusline), take
  // the scanner reset from those rows ALONE. Transcript-inferred rows carry a
  // per-home lookback estimate whose reset can sit later than the account's real
  // one, and the maxIso below would then hand the page a reset the seat's own
  // status line disagrees with (Tim nearly idled a seat mid-window over it).
  const authoritativeRows = currentRows.filter(isAuthoritativeQuotaSource);
  const resetBasis =
    authoritativeRows.length > 0 ? authoritativeRows : currentRows;
  const overrideActive = overrideResets.length > 0;
  // Estimate-only window: the lookback reset (oldest message seen + window)
  // lands near "now" on a busy account and rolls forward forever, which read
  // as "resets in 20m" and sorted first (Tim, 2026-09-26). On a fixed-cadence
  // window, roll the last API reset forward by whole periods instead.
  const resetEstimated = !overrideActive && authoritativeRows.length === 0;
  const projectedReset =
    resetEstimated && cadenceMs !== null && currentRows.length > 0
      ? projectFromLastApiReset(
          windowRows
            .filter(isAuthoritativeQuotaSource)
            .map((r) => parseUtcMs(effectiveResetIso(r))),
          cadenceMs,
          now
        )
      : null;
  const effectiveReset = overrideActive
    ? minIso(overrideResets)
    : (projectedReset ?? maxIso(resetBasis.map((r) => effectiveResetIso(r))));
  const activeHomes = currentRows.filter((r) => (r.tokenCount ?? 0) > 0).length;
  // Percent used: the Anthropic API's own figure (apiPercentUsed, stamped on
  // rows from a fresh seat payload) is authoritative. The transcript-token
  // sum divided by an ASSUMED cap is a guess on both ends and stays only as
  // fallback for accounts with no live payload. Freshest stamp wins, from the
  // authoritative rows alone when any exist (Bug 34586, same rule as resetBasis).
  const apiRows = currentRows.filter((r) => r.apiPercentUsed !== null);
  const authoritativeApiRows = apiRows.filter(isAuthoritativeQuotaSource);
  const percentBasis =
    authoritativeApiRows.length > 0 ? authoritativeApiRows : apiRows;
  const freshestApiRow =
    percentBasis.length > 0
      ? percentBasis.reduce((best, r) =>
          observationTime(r) > observationTime(best) ? r : best
        )
      : null;
  const apiPercent = freshestApiRow?.apiPercentUsed ?? null;
  const percentUsed =
    apiPercent !== null
      ? Math.min(100, apiPercent)
      : cap > 0
        ? Math.min(100, (tokenCount / cap) * 100)
        : 0;
  // Source rides the same observation as percent, matching the C# rollup;
  // a disagreement between the rendered status line and the payload
  // cross-check is a flag for the page, never silently resolved here.
  const disagreementNote =
    currentRows
      .filter((r) => !!r.disagreementNote)
      .sort((a, b) =>
        (b.lastUpdatedAt ?? '').localeCompare(a.lastUpdatedAt ?? '')
      )[0]?.disagreementNote ?? null;
  // No current row = no live window. Keep the freshest EXPIRED reading so the
  // page renders "no active window (last seen N% at T)" instead of the
  // live-looking 0% bar that hid the parts incident.
  const staleRows = windowRows.filter(
    (r) => !isQuotaRowCurrent(r, now) && r.apiPercentUsed !== null
  );
  const freshestStale =
    currentRows.length === 0 && staleRows.length > 0
      ? staleRows.reduce((best, r) =>
          observationTime(r) > observationTime(best) ? r : best
        )
      : null;
  return {
    tokenCount,
    messageCount,
    firstMessageAt: earliestFirst,
    effectiveResetAt: effectiveReset,
    resetEstimated,
    activeHomes,
    overrideActive,
    cap,
    percentUsed,
    band: classifyUsageBand(percentUsed),
    source: freshestApiRow?.source ?? null,
    capturedAt: toUtcIso(
      freshestApiRow?.capturedAt ?? freshestApiRow?.lastUpdatedAt ?? null
    ),
    disagreementNote,
    staleObservation: freshestStale
      ? {
          percent: freshestStale.apiPercentUsed as number,
          at: observationStamp(freshestStale) ?? '',
        }
      : null,
  };
}

/**
 * Group quota rows per account and summarize both windows. Hottest accounts
 * surface first, compared by the larger of the two window percentages so an
 * account at 80% weekly outranks one at 50% on both.
 */
export function aggregateQuotaRollups(
  rows: QuotaRow[],
  now = Date.now(),
  provider: 'claude' | 'grok' | 'codex' = 'claude'
): AccountRollup[] {
  const byAccount = new Map<string, QuotaRow[]>();
  for (const row of rows) {
    const rowProvider = row.provider ?? 'claude';
    if (rowProvider !== provider) continue;
    const account = row.accountUpn.toLowerCase();
    const list = byAccount.get(account) ?? [];
    list.push(row);
    byAccount.set(account, list);
  }

  const rollups: AccountRollup[] = [];
  for (const accountRows of byAccount.values()) {
    rollups.push({
      accountUpn: accountRows[0].accountUpn,
      fiveHour: summarizeQuotaWindow(
        accountRows.filter((r) => r.windowType === 'FiveHour'),
        TEAM_PREMIUM_FIVE_HOUR_CAP,
        now
      ),
      weekly: summarizeQuotaWindow(
        accountRows.filter((r) => r.windowType === 'Weekly'),
        TEAM_PREMIUM_WEEKLY_CAP,
        now,
        WEEKLY_WINDOW_MS
      ),
      // Fable is status-line-only: cap 0 means the percent can ONLY come from
      // apiPercentUsed; there is no transcript token split to estimate from.
      fable: summarizeQuotaWindow(
        accountRows.filter((r) => r.windowType === 'WeeklyFable'),
        0,
        now,
        WEEKLY_WINDOW_MS
      ),
      totalHomes: new Set(accountRows.map((r) => r.reportingHomeId)).size,
      lastUpdatedAt: maxLastUpdated(accountRows),
    });
  }

  rollups.sort((a, b) => hottestCompare(a, b, -1));
  return rollups;
}

/** True while the account has at least one LIVE window to exploit. Fable
 * counts: it is status-line-only (token counts stay 0 by design), so a
 * fable-only live account must not read as windowless (grok residual on
 * PR 2768). */
export function hasLiveWindow(r: AccountRollup): boolean {
  return (
    r.fiveHour.effectiveResetAt !== null ||
    r.weekly.effectiveResetAt !== null ||
    r.fable.effectiveResetAt !== null
  );
}

/**
 * Hottest-first comparison with the WI 31105 partition: an account with NO
 * live window is never "next to exploit", so it sorts after every account
 * that has one, in BOTH directions. Inside each partition, the larger of the
 * two all-model window percentages decides.
 */
function hottestCompare(
  a: AccountRollup,
  b: AccountRollup,
  sign: number
): number {
  const aLive = hasLiveWindow(a);
  const bLive = hasLiveWindow(b);
  if (aLive !== bLive) return aLive ? -1 : 1;
  const aMax = Math.max(a.fiveHour.percentUsed, a.weekly.percentUsed);
  const bMax = Math.max(b.fiveHour.percentUsed, b.weekly.percentUsed);
  return sign * (aMax - bMax);
}

export type RollupSortKey =
  | 'hottest'
  | 'account'
  | 'fiveHourTokens'
  | 'fiveHourReset'
  | 'weeklyTokens'
  | 'weeklyReset'
  | 'fableReset';

export type SortDirection = 'asc' | 'desc';

/**
 * Sort rollups for display. Reset-time sorts put accounts with NO live window
 * last in both directions: a null reset is "nothing to wait for", not
 * "resets soonest", and surfacing it first would misread as the next window
 * to exploit.
 */
export function sortAccountRollups(
  rollups: AccountRollup[],
  key: RollupSortKey,
  dir: SortDirection = 'asc'
): AccountRollup[] {
  const sign = dir === 'asc' ? 1 : -1;
  const byNullableMs = (
    a: string | null,
    b: string | null,
    s: number
  ): number => {
    const aMs = parseUtcMs(a);
    const bMs = parseUtcMs(b);
    if (aMs === null && bMs === null) return 0;
    if (aMs === null) return 1;
    if (bMs === null) return -1;
    return (aMs - bMs) * s;
  };
  const sorted = [...rollups];
  switch (key) {
    case 'account':
      sorted.sort((a, b) => sign * a.accountUpn.localeCompare(b.accountUpn));
      break;
    case 'fiveHourTokens':
      sorted.sort(
        (a, b) => sign * (a.fiveHour.tokenCount - b.fiveHour.tokenCount)
      );
      break;
    case 'weeklyTokens':
      sorted.sort((a, b) => sign * (a.weekly.tokenCount - b.weekly.tokenCount));
      break;
    case 'fiveHourReset':
      sorted.sort((a, b) =>
        byNullableMs(
          a.fiveHour.effectiveResetAt,
          b.fiveHour.effectiveResetAt,
          sign
        )
      );
      break;
    case 'weeklyReset':
      sorted.sort((a, b) =>
        byNullableMs(a.weekly.effectiveResetAt, b.weekly.effectiveResetAt, sign)
      );
      break;
    case 'fableReset':
      sorted.sort((a, b) =>
        byNullableMs(a.fable.effectiveResetAt, b.fable.effectiveResetAt, sign)
      );
      break;
    case 'hottest':
    default:
      // WI 31105: no-live-window accounts are never "next to exploit"; they
      // partition last in BOTH directions (hottestCompare).
      sorted.sort((a, b) => hottestCompare(a, b, sign));
      break;
  }
  return sorted;
}

export interface RollupFilter {
  /** Case-insensitive substring match on the account UPN. */
  search?: string;
  /** Drop accounts with zero current tokens in both windows. */
  hideIdle?: boolean;
}

export function filterAccountRollups(
  rollups: AccountRollup[],
  filter: RollupFilter
): AccountRollup[] {
  const needle = filter.search?.trim().toLowerCase() ?? '';
  return rollups.filter((r) => {
    if (needle && !r.accountUpn.toLowerCase().includes(needle)) return false;
    // Fable has no token split (cap 0), so a token-count-only idle test would
    // hide a fable-only LIVE account; a live fable window keeps the row.
    if (
      filter.hideIdle &&
      r.fiveHour.tokenCount === 0 &&
      r.weekly.tokenCount === 0 &&
      r.fable.effectiveResetAt === null
    ) {
      return false;
    }
    return true;
  });
}

export interface SeatShare {
  homeId: string;
  host: string | null;
  tokenCount: number;
  messageCount: number;
  /** This seat's share of the account's current-window tokens, 0-100. */
  sharePct: number;
}

/**
 * Which seats are eating an account's current window, largest burner first.
 * Mirrors the vanilla CC "Seats" column on the accounts panel.
 */
export function accountSeatShares(
  rows: QuotaRow[],
  accountUpn: string,
  windowType: QuotaRow['windowType'],
  now = Date.now()
): SeatShare[] {
  const current = rows.filter(
    (r) =>
      r.accountUpn.toLowerCase() === accountUpn.toLowerCase() &&
      r.windowType === windowType &&
      isQuotaRowCurrent(r, now) &&
      (r.tokenCount ?? 0) > 0
  );
  const total = current.reduce((s, r) => s + (r.tokenCount ?? 0), 0);
  return current
    .map((r) => ({
      homeId: r.reportingHomeId ?? '(unknown home)',
      host: r.reportingHost,
      tokenCount: r.tokenCount ?? 0,
      messageCount: r.messageCount ?? 0,
      sharePct: total > 0 ? ((r.tokenCount ?? 0) / total) * 100 : 0,
    }))
    .sort((a, b) => b.tokenCount - a.tokenCount);
}

/**
 * SQL DECIMAL(5,2) columns come back as strings from some JSON serializers;
 * coerce the numeric fields, preserving nulls on the nullable ones.
 */
export function normalizeContextSnapshot(r: ContextSnapshot): ContextSnapshot {
  return {
    ...r,
    percentUsed:
      typeof r.percentUsed === 'string' ? Number(r.percentUsed) : r.percentUsed,
    totalTokens:
      typeof r.totalTokens === 'string' ? Number(r.totalTokens) : r.totalTokens,
    windowSize:
      typeof r.windowSize === 'string' ? Number(r.windowSize) : r.windowSize,
    fiveHourPercent:
      r.fiveHourPercent == null ? null : Number(r.fiveHourPercent),
    sevenDayPercent:
      r.sevenDayPercent == null ? null : Number(r.sevenDayPercent),
  };
}

export function classifyContextBand(s: ContextSnapshot): ContextBand {
  if (s.cycleStatus === 'cycle_prepared') return 'prepared';
  if (s.cycleStatus === 'cycle_requested') return 'requested';
  if (s.percentUsed >= CONTEXT_THRESHOLD_PCT) return 'over';
  if (s.percentUsed >= CONTEXT_THRESHOLD_PCT - 10) return 'warn';
  return 'safe';
}

/** `now` is injectable for tests; defaults to wallclock. */
export function toContextRow(s: ContextSnapshot, now = Date.now()): ContextRow {
  const observedAt = s.observedAt ? new Date(s.observedAt).getTime() : now;
  return {
    ...s,
    band: classifyContextBand(s),
    windowLabel: s.windowSize >= 1_000_000 ? '1M' : '200K',
    ageMinutes: Math.round((now - observedAt) / 60000),
  };
}
